import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/i18n/LanguageContext";
import {
  PHONE_E164,
  PHONE_DISPLAY,
  WHATSAPP_URL,
  VIBER_URL,
  WhatsAppGlyph,
  ViberGlyph,
} from "@/components/ContactChannels";

const HERO_IMG =
  "https://images.unsplash.com/photo-1571753753463-636443ad284c?auto=format&fit=crop&w=2400&q=80";

export default function Request() {
  const { t } = useI18n();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", phone: "", car_preference: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const pref = params.get("preference");
    if (pref) setForm((f) => ({ ...f, car_preference: pref }));
  }, [params]);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const vehicle_id = params.get("vehicle") || null;
      await api.post("/inquiries", {
        name: form.name,
        phone: form.phone,
        email: "",
        car_preference: form.car_preference,
        budget: "",
        message: "",
        vehicle_id,
      });
      setSuccess(true);
      toast.success(t("request.toast.success"));
      setForm({ name: "", phone: "", car_preference: "" });
    } catch (err) {
      toast.error(
        formatApiError(err.response?.data?.detail) || t("request.toast.error")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="request-page">
      {/* HERO */}
      <section
        className="relative h-[78svh] min-h-[520px] w-full overflow-hidden bg-[#0B0B0B]"
        data-testid="request-hero"
      >
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-[78%_center] md:object-[85%_center] portrait:scale-[1.15]"
        />
        {/* Darkeners — keep heading column readable on every breakpoint */}
        <div
          className="absolute inset-0 pointer-events-none md:hidden"
          style={{
            background:
              "linear-gradient(to right, rgba(11,11,11,0.95) 0%, rgba(11,11,11,0.7) 45%, rgba(11,11,11,0.25) 100%)",
          }}
        />
        <div
          className="absolute inset-0 pointer-events-none hidden md:block"
          style={{
            background:
              "linear-gradient(to right, rgba(11,11,11,0.88) 0%, rgba(11,11,11,0.4) 55%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 top-0 pointer-events-none"
          style={{
            height: "clamp(180px, 30vh, 360px)",
            background:
              "linear-gradient(to bottom, #0B0B0B 0%, rgba(11,11,11,0.7) 60%, transparent 100%)",
          }}
        />
        <div
          className="absolute inset-x-0 bottom-0 pointer-events-none"
          style={{
            height: "55%",
            background:
              "linear-gradient(to top, #0B0B0B 0%, rgba(11,11,11,0.7) 35%, transparent 100%)",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 h-full flex flex-col justify-end pb-16 md:pb-24">
          <div className="max-w-2xl fade-up">
            <div className="section-label mb-5">{t("request.label")}</div>
            <h1 className="font-display text-[2.1rem] leading-[1.08] sm:text-5xl lg:text-6xl font-light tracking-tight text-white">
              {t("request.title.part1")}{" "}
              <span className="italic text-[#C6A15B]">
                {t("request.title.accent")}
              </span>
              .
            </h1>
            <p className="mt-6 text-sm md:text-base text-zinc-300 leading-relaxed max-w-xl">
              {t("request.intro")}
            </p>
          </div>
        </div>
      </section>

      {/* MINIMAL FORM */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-28">
        {success ? (
          <div
            className="border border-[#C6A15B]/30 bg-[#0F0F0F] p-10 md:p-14 text-center"
            data-testid="request-success"
          >
            <div className="w-14 h-14 rounded-full border border-[#C6A15B]/40 text-[#C6A15B] mx-auto flex items-center justify-center">
              <CheckCircle2 size={22} />
            </div>
            <h2 className="font-display text-3xl mt-6">
              {t("request.success.title")}
            </h2>
            <p className="text-zinc-400 text-sm mt-3 max-w-md mx-auto">
              {t("request.success.subtitle")}
            </p>
            <button
              className="btn-ghost mt-10"
              onClick={() => setSuccess(false)}
              data-testid="request-new"
            >
              {t("request.success.again")}
            </button>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="space-y-8 md:space-y-10"
            data-testid="request-form"
          >
            <div>
              <label className="ec-label" htmlFor="name">
                {t("request.form.name")}
              </label>
              <input
                id="name"
                name="name"
                required
                value={form.name}
                onChange={onChange}
                className="ec-input !bg-transparent !border-0 !border-b !border-white/15 !rounded-none !px-0 !py-3 focus:!border-[#C6A15B]"
                placeholder={t("request.form.name.placeholder")}
                data-testid="input-name"
              />
            </div>

            <div>
              <label className="ec-label" htmlFor="phone">
                {t("request.form.phone")}
              </label>
              <input
                id="phone"
                name="phone"
                required
                value={form.phone}
                onChange={onChange}
                className="ec-input !bg-transparent !border-0 !border-b !border-white/15 !rounded-none !px-0 !py-3 focus:!border-[#C6A15B]"
                placeholder={t("request.form.phone.placeholder")}
                data-testid="input-phone"
              />
            </div>

            <div>
              <label className="ec-label" htmlFor="car_preference">
                {t("request.form.preference")}
              </label>
              <textarea
                id="car_preference"
                name="car_preference"
                required
                value={form.car_preference}
                onChange={onChange}
                rows={3}
                className="ec-input resize-none !bg-transparent !border-0 !border-b !border-white/15 !rounded-none !px-0 !py-3 focus:!border-[#C6A15B]"
                placeholder={t("request.form.preference.placeholder")}
                data-testid="input-preference"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="btn-gold disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="submit-request"
            >
              {submitting ? t("request.form.submitting") : t("request.form.submit")}{" "}
              <ArrowRight size={14} />
            </button>
          </form>
        )}
      </section>

      {/* DIRECT CONTACT CHANNELS — WhatsApp / Viber / Phone / Email */}
      <section
        className="border-t border-white/5 bg-[#0B0B0B]"
        data-testid="request-channels"
      >
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
            <ChannelRow
              icon={<WhatsAppGlyph size={18} />}
              label={t("request.channels.whatsapp")}
              value={PHONE_DISPLAY}
              href={WHATSAPP_URL}
              external
              testId="channel-whatsapp"
            />
            <ChannelRow
              icon={<ViberGlyph size={18} />}
              label={t("request.channels.viber")}
              value={PHONE_DISPLAY}
              href={VIBER_URL}
              testId="channel-viber"
            />
            <ChannelRow
              icon={<Phone size={16} />}
              label={t("request.channels.phone")}
              value={PHONE_DISPLAY}
              href={`tel:${PHONE_E164}`}
              testId="channel-phone"
            />
            <ChannelRow
              icon={<Mail size={16} />}
              label={t("request.channels.email")}
              value="info@elysiancars.com"
              href="mailto:info@elysiancars.com"
              testId="channel-email"
            />
          </div>

          <p
            className="mt-16 md:mt-20 text-[10px] tracking-[0.36em] uppercase text-zinc-500 text-center"
            data-testid="request-note"
          >
            {t("request.note")}
          </p>
        </div>
      </section>
    </div>
  );
}

function ChannelRow({ icon, label, value, href, external = false, testId }) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="group flex items-start gap-4 transition-colors"
      data-testid={testId}
    >
      <span className="text-[#C6A15B] mt-1">{icon}</span>
      <span className="flex flex-col">
        <span className="text-[10px] tracking-[0.32em] uppercase text-zinc-500">
          {label}
        </span>
        <span className="text-white text-base mt-1 group-hover:text-[#C6A15B] transition-colors">
          {value}
        </span>
      </span>
    </a>
  );
}
